/* ===================================================================
   useRequests() + useCreateRequests() -- the review-request records
   (Hopper, 2026-06-17).

   useRequests()       -> GET  /api/v1/requests: recent request rows for
                          the Requests screen "Recent requests" table.
   useCreateRequests() -> POST /api/v1/requests: create request record(s)
                          for one or more customers.

   HARD PRODUCT RULE -- DRY-RUN: createRequests PERSISTS real public.requests
   rows (status 'queued') but NO SMS/email is transmitted (live sending is
   gated until Twilio is approved; response.dry_run is true). It fires ONLY
   from an explicit operator click. After a create we invalidate the requests
   list + the requests funnel summary so the Requests screen reflects the new
   rows immediately.

   AUTH: api.ts attaches the Bearer rr_ token; the read hook is disabled
   until a token exists so it never auto-fires a 401.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, type RequestsCreated, type RequestsResponse } from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useRequests(
  params: { limit?: number; enabled?: boolean } = {},
): UseQueryResult<RequestsResponse, Error> {
  const { enabled, ...query } = params;
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.requests(query),
    queryFn: ({ signal }) => api.listRequests(query, signal),
    enabled: hasToken && enabled !== false,
    staleTime: 15_000,
  });
}

export interface UseCreateRequestsResult {
  createRequests: (input: {
    customer_ids: string[];
    channel?: 'sms' | 'email';
    subject?: string;
    message?: string;
  }) => Promise<RequestsCreated>;
  isPending: boolean;
  error: Error | null;
}

export function useCreateRequests(): UseCreateRequestsResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: {
      customer_ids: string[];
      channel?: 'sms' | 'email';
      subject?: string;
      message?: string;
    }) => api.createRequests(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.requests() });
      void qc.invalidateQueries({ queryKey: qk.requestsSummary() });
      void qc.invalidateQueries({ queryKey: qk.dashboardSummary() });
    },
  });
  return {
    createRequests: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
